/* eslint-disable import/no-cycle, react/no-unused-prop-types */
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Switch,
  VStack,
  Text,
  ButtonGroup,
  Center,
  Spinner,
} from '@chakra-ui/core';
import { SearchIcon } from '@chakra-ui/icons';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { SortingRule } from 'react-table';
import { VirtualTable } from '../../../../app/components/VirtualTable';
import { CreateTemplateButton } from '../modal/components/CreateTemplateButton';
import { QuestionnaireTemplateListResponse, SortDirection, SortField, PaginationQueryType } from '../questionnaireTemplate.types';
import { useTemplateColumns } from './tableColumns/tableColumns';

type TableProps = {
  data: QuestionnaireTemplateListResponse[];
  showDefaultTemplates: boolean;
  onToggleShowDefaultTemplates: (checked: boolean) => void;
  onCreateNewTemplate: () => void;
  onOpenTemplate: (template: QuestionnaireTemplateListResponse) => void;
  sortField: SortField;
  sortDirection: SortDirection;
  handleSortChange: (column: SortField) => void;
  sortDisabled?: boolean;
  pagination: PaginationQueryType;
  handleNextPage: () => void;
  handlePreviousPage: () => void;
  handleLimitChange: (limit: number) => void;
  PaginationLimit: React.ComponentType<{ limit: number | null; onChange: (limit: number) => void }>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  isFetching: boolean;
  showToggle?: boolean;
};

function QuestionnaireTemplateTableView({
  data,
  showDefaultTemplates,
  onToggleShowDefaultTemplates,
  onOpenTemplate,
  sortField,
  sortDirection,
  handleSortChange,
  sortDisabled,
  pagination,
  handleNextPage,
  handlePreviousPage,
  handleLimitChange,
  PaginationLimit,
  searchQuery,
  onSearchChange,
  isFetching,
  showToggle,
}: TableProps) {
  const { t } = useTranslation();
  const columns = useTemplateColumns(onOpenTemplate, sortDisabled);
  const sortByRule = useMemo<SortingRule<QuestionnaireTemplateListResponse>[]>(() => {
    if (sortDisabled) {
      return [{ id: 'positionCount', desc: true }];
    }
    return [{ id: sortField || 'positionCount', desc: sortDirection === 'desc' }];
  }, [sortDisabled, sortField, sortDirection]);

  function getPaginationText() {
    // istanbul ignore next
    if (data.length === 0) {
      return '';
    }
    if (pagination.total === 1) {
      return t('hrFormTemplate:templateListView.paginationText.showingOneResult', {
        currentItems: data.length,
        totalItems: pagination.total,
      });
    }
    return t('hrFormTemplate:templateListView.paginationText.showingResult', {
      currentItems: data.length,
      totalItems: pagination.total,
    });
  }

  const handleTableSort = (columnId: string) => {
    if (sortDisabled) {
      return;
    }
    handleSortChange(columnId as SortField);
  };

  return (
    <Box bg="white" w="100%" p={6}>
      <VStack spacing={4} align="stretch">
        <Flex justify="space-between" pb={3} borderBottom="1px solid lavenderMist">
          <Heading fontSize="lg">{t('administration:questionnaireTemplate.title')}</Heading>

          <CreateTemplateButton />
        </Flex>
        <HStack justify="space-between" pb={4} borderBottom="1px solid lavenderMist">
          <InputGroup maxW="60vw">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t('administration:questionnaireTemplate.searchText')}
            />
          </InputGroup>

          {showToggle && (
            <FormControl display="flex" w="auto">
              <FormLabel mb="0" fontSize="sm">
                {t('administration:questionnaireTemplate.showDefaultTemplates')}
              </FormLabel>

              <Switch
                isChecked={showDefaultTemplates}
                onChange={(e) => onToggleShowDefaultTemplates(e.target.checked)}
                colorScheme="blue"
              />
            </FormControl>
          )}
        </HStack>

        <Box position="relative">
          <VirtualTable
            data={data}
            columns={columns}
            getDataId={(row) => row.id}
            rowHeight={56}
            tableHeight="55vh"
            sortBy={sortByRule}
            onSort={handleTableSort}
            disableSortReset
            tableHeadProps={{ enableSortingIcons: !sortDisabled }}
            emptyTable={
              <Box py={10} textAlign="center">
                <Text color="gray.500">{t('administration:questionnaireTemplate.noTemplatesFound')}</Text>
              </Box>
            }
          />
          {isFetching && (
            <Center position="absolute" top="0" left="0" right="0" bottom="0" bg="rgba(255,255,255,0.6)" zIndex={1}>
              <Spinner size="lg" data-testid="loading-spinner" color="blue.500" />
            </Center>
          )}
        </Box>
        <Box backgroundColor="white" padding={2}>
          {data.length > 0 && (
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.500">
                {getPaginationText()}
              </Text>
              <HStack spacing={4}>
                <PaginationLimit limit={pagination.limit} onChange={handleLimitChange} />
                <ButtonGroup variant="outline" size="xs" color="gray" padding={0}>
                  <Button disabled={!pagination.hasPrevious} onClick={handlePreviousPage}>
                    <IoChevronBack />
                  </Button>
                  <Button disabled={!pagination.hasNext} onClick={handleNextPage}>
                    <IoChevronForward />
                  </Button>
                </ButtonGroup>
              </HStack>
            </HStack>
          )}
        </Box>
      </VStack>
    </Box>
  );
}
QuestionnaireTemplateTableView.defaultProps = {
  sortDisabled: false,
  showToggle: true,
};

export default QuestionnaireTemplateTableView;
